import { Tool } from './Tool'
import MapBrowserEvent from 'ol/MapBrowserEvent'
import PointerInteraction from 'ol/interaction/Pointer'
import { Control } from 'ol/control'
import { Nullable } from '@/shared/types/app.types'
import MapEvent from 'ol/MapEvent'
import EventType from 'ol/events/EventType'

/**
 * This code is a modification of the DragRotateAndZoom class from open layers code
 * The intention was to remove the zoom functionality, and just apply the rotation.
 * Other interactions implemented in the openlayers with rotation has several issues, and DragRotateAndZoom was perfect without the zoom part.
 * That's the reason why we bring this code here.
 *
 * Original code:
 * https://github.com/openlayers/openlayers/blob/ab8a16a8a716c6f65b515647b990ef37c73d5ae4/src/ol/interaction/DragRotateAndZoom.js
 *
 */
class DragRotateCustom extends PointerInteraction {
    private lastAngle: number | undefined
    private condition = (event: MapBrowserEvent<UIEvent>) => {
        return event.type === 'pointerdown'
    }

    handleDragEvent(mapBrowserEvent: MapBrowserEvent<MouseEvent>):void {
        const map = mapBrowserEvent.map
        const size = map.getSize() ?? [0, 0]
        const offset = mapBrowserEvent.pixel
        const deltaX = offset[0] - size[0] / 2
        const deltaY = size[1] / 2 - offset[1]
        const theta = Math.atan2(deltaY, deltaX)
        const view = map.getView()
        if (this.lastAngle !== undefined) {
            const angleDelta = this.lastAngle - theta
            view.adjustRotationInternal(angleDelta)
        }
        this.lastAngle = theta
    }

    handleDownEvent(mapBrowserEvent: MapBrowserEvent<MouseEvent>): boolean {
        if (this.condition(mapBrowserEvent)) {
            this.lastAngle = undefined
            return true
        } else {
            return false
        }
    }
}

export default class RotationTool extends Tool {
    private rotateInteraction!: DragRotateCustom;

    public removeInteractions(): void {
        if (this.rotateInteraction) { this.state.map?.removeInteraction(this.rotateInteraction) }
    }

    public addInteractions(): void {
        this.rotateInteraction = new DragRotateCustom()
        this.state.map?.addInteraction(this.rotateInteraction)
    }
}

export class RotateCustom extends Control {
    private compassBase: HTMLElement
    private compassIndicator: HTMLElement
    private compassLabel: HTMLElement
    private rotation: number = 0
    private didMouseDown:boolean=false

    constructor(compassBase:HTMLElement, compassIndicator:HTMLElement, compassLabel:HTMLElement) {
        super({
            element: compassBase
        })
        this.compassBase = compassBase
        this.compassIndicator = compassIndicator
        this.compassLabel = compassLabel

        this.compassLabel.innerHTML = `${this.rotation}&deg;`

        compassBase.addEventListener(
            'mousedown',
            this.handleMouseDown.bind(this),
            false
        )
        compassBase.addEventListener(
            'mouseup',
            this.handleMouseUp.bind(this),
            false
        )

        compassBase.addEventListener(
            'mousemove',
            this.handleMouseMove.bind(this),
            false
        )

        compassBase.addEventListener(
            'mouseenter',
            this.handleMouseEnter.bind(this),
            false
        )

        compassBase.addEventListener(
            'click',
            this.handleClick.bind(this),
            false
        )
        compassBase.addEventListener(
            'wheel',
            this.handleWheel.bind(this),
            false
        )
    }

    handleMouseDown(event: MouseEvent): void {
        this.didMouseDown = true
    }

    handleMouseEnter(event: MouseEvent): void {
        if (event.buttons === 0) {
            this.didMouseDown = false
        }
    }

    handleMouseUp(event: MouseEvent): void {
        this.didMouseDown = false
    }

    handleMouseMove(event: MouseEvent): void {
        if (this.didMouseDown) {
            this.handleClick(event)
        }
    }

    handleWheel(event: WheelEvent): void {
        const map = this.getMap()
        if (map) {
            const view = map.getView()
            view.setRotation(view.getRotation() + 0.1 * (event.deltaY < 0 ? -1 : 1))
        }
    }

    handleClick(event: MouseEvent): void {
        event.preventDefault()
        const centerPoint = [this.compassBase.offsetWidth / 2, this.compassBase.offsetHeight / 2]

        var rect = (event.currentTarget as any).getBoundingClientRect()
        var mouseX = event.clientX - rect.left // x position within the element.
        var mouseY = event.clientY - rect.top // y position within the element.

        const degrees = (Math.atan2(mouseY - centerPoint[1], mouseX - centerPoint[0]) * 180 / Math.PI) + 90

        const map = this.getMap()
        if (map) {
            const view = map.getView()
            view.setRotation(degrees * (Math.PI / 180))
        }
    }

    render(mapEvent: MapEvent): void {
        const frameState = mapEvent.frameState
        if (!frameState) {
            return
        }
        const rotation = frameState.viewState.rotation
        if (rotation !== this.rotation) {
            const transform = `rotate(${rotation}rad)`
            this.compassIndicator.style.transform = transform
            let angle = Math.trunc(rotation * 180 / Math.PI)
            if (angle < 0) {
                angle = 360 + angle
            }
            this.compassLabel.innerHTML = `${angle}&deg;`
        }
        this.rotation = rotation
    }
}
